import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { resetPassword } from "@/lib/supabase/auth";
import { toast } from "sonner";
import { debug } from "@/lib/utils";
import TradesPurpleLayout from "../components/Layout";
import { Helmet } from "react-helmet-async";

const schema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirm: z.string(),
}).refine((d) => d.password === d.confirm, {
  message: "Passwords don't match",
  path: ["confirm"],
});
type FormValues = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  const onSubmit = async (data: FormValues) => {
    if (!token) {
      toast.error("Reset link is missing or invalid");
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword(token, data.password);
      toast.success("Password reset — you're logged in");
      navigate("/");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Reset failed";
      toast.error(message);
      debug.error("Reset error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>TradesPurple | Reset password</title>
      </Helmet>

      <div className="flex items-center justify-center">
        <Card className="w-full border-2 border-purple-900 rounded-xl shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center" style={{ color: "#5D4A9C" }}>
              Set a new password
            </CardTitle>
            <CardDescription className="text-center text-purple-700">
              {token ? "Choose a new password for your account." : "This reset link is missing a token."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {token && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel style={{ color: "#5D4A9C" }}>New password</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••"
                            className="border-2 border-purple-300 focus:border-purple-500"
                            {...field} disabled={isLoading} />
                        </FormControl>
                        <FormMessage className="text-red-600" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirm"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel style={{ color: "#5D4A9C" }}>Confirm password</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••"
                            className="border-2 border-purple-300 focus:border-purple-500"
                            {...field} disabled={isLoading} />
                        </FormControl>
                        <FormMessage className="text-red-600" />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full bg-purple-700 hover:bg-purple-800"
                    disabled={isLoading}
                  >
                    {isLoading ? "Setting password..." : "Set password and log in"}
                  </Button>
                </form>
              </Form>
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <div className="text-sm text-center text-purple-700">
              <Link to="/auth/login" style={{ color: "#5D4A9C" }} className="font-semibold hover:underline">
                Back to login
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </TradesPurpleLayout>
  );
}
